import { Button } from "components/Button/Button"
import { Checkbox } from "components/Checkbox/Checkbox"
import { Disclosure } from "components/Disclosure/Disclosure"
import { Divider } from "components/Divider/Divider"
import { useNotification } from "components/Notification/Notification"
import { SearchInput } from "components/SearchInput/SearchInput"
import { Space } from "components/Space/Space"
import { Spinner } from "components/Spinner/Spinner"
import { useDisclosure } from "hooks/useDisclosure"
import { useInfiniteScroll } from "hooks/useInfiniteScroll"
import { useListSort } from "hooks/useListSort"
import { useSpotlight } from "hooks/useSpotlight"
import { Visit } from "interface/visit"
import { Chat } from "modules/communications/Chat"
import { Voice } from "modules/communications/Voice"
import { AlternateContactsList } from "modules/patient/AlternateContactsList"
import { CancelVisit } from "modules/visits/CancelVisit"
import { EditVisit } from "modules/visits/EditVisit"
import { ComponentProps, useRef } from "react"
import { useSearchParams } from "react-router"
import { useTeam } from "services/users"
import { useVisits } from "services/visits"
import { cn } from "styles/cn"
import { asDate, getTimeZone } from "utils/date"
import { prettyprintPhoneNumber } from "utils/patient"
import { prettyVisitInterval } from "utils/visits"

export function AllVisits() {
	const targetScroll = useRef<HTMLUListElement>(null)
	const team = useTeam()
	const [searchParams, setSearchParams] = useSearchParams(DEFAULT_SEARCH_PARAMS)
	const query = searchParams.get("query") ?? ""
	const onlyUnreadMessages = searchParams.get("onlyUnreadMessages") === "true"
	const { sort, toggle, sortIcon, labelSort } = useListSort<Visit>(DEFAULT_SORT)
	const {
		data: { pages: visits = [] } = {},
		hasNextPage,
		fetchNextPage,
		isFetching,
		isFetchingNextPage,
	} = useVisits({
		status: ["SCHEDULED"],
		sort,
		query,
		onlyUnreadMessages,
	})
	const { isFocused } = useSpotlight()
	const isLoading = isFetching || isFetchingNextPage
	const canEdit = team?.scheduling === "edit"

	useInfiniteScroll({
		target: targetScroll.current,
		hasMore: hasNextPage,
		isLoading,
		onLoadMore: fetchNextPage,
	})

	return (
		<Spinner spinning={isFetching}>
			<div className="@container mx-auto flex h-full max-h-full max-w-300 flex-col gap-3 overflow-hidden">
				<h2 className="font-nunito text-3xl font-semibold">All visits</h2>

				<div className="flex items-center gap-2">
					<SearchInput
						value={query}
						onSearch={(query) => setSearchParams({ query })}
					/>
					<Checkbox
						checked={onlyUnreadMessages}
						className="min-w-fit"
						onChange={(e) =>
							setSearchParams({
								query,
								onlyUnreadMessages: String(e.target.checked),
							})
						}
					>
						Only unread messages
					</Checkbox>
				</div>

				<Space.Compact className="justify-end @6xl:hidden">
					<Button
						icon={sortIcon("visitDate")}
						aria-label={labelSort("visitDate", "Visit date & time")}
						onClick={() => toggle("visitDate")}
					>
						Visit date & time
					</Button>
				</Space.Compact>

				<div className="flex flex-col gap-4 overflow-hidden">
					<div className="hidden gap-4 rounded-lg bg-mint-0 p-5 py-3 @6xl:flex">
						<div
							className={cn(
								"grid w-full grid-cols-[140px_240px_minmax(max-content,360px)_auto] items-center gap-3 font-bold",
								canEdit &&
									"grid-cols-[140px_240px_minmax(max-content,360px)_200px_auto]",
							)}
						>
							<div role="columnheader" className="whitespace-break-spaces">
								Visit type
							</div>
							<div
								role="columnheader"
								className="flex items-center gap-2 whitespace-break-spaces"
							>
								Visit date & time
								<Button
									size="small"
									type="dashed"
									icon={sortIcon("visitDate")}
									aria-label={labelSort("visitDate", "Visit date & time")}
									onClick={() => toggle("visitDate")}
								/>
							</div>
							<div
								role="columnheader"
								className="flex items-center gap-2 whitespace-break-spaces"
							>
								Contact patient
							</div>
							{canEdit && (
								<div role="columnheader" className="whitespace-break-spaces">
									Actions
								</div>
							)}
							<div className="invisible size-3.5" />
						</div>
					</div>

					<ul
						ref={targetScroll}
						className="flex flex-col gap-4 overflow-y-auto"
						aria-label={
							isLoading
								? "Waiting for scheduled visits"
								: `${visits.length} scheduled visit(s)`
						}
					>
						{visits.map((visit) => {
							const { id, visitType, patient } = visit
							const visitDate = asDate(visit.visitDate!)
							const details = [
								["Patient's full name", patient?.fullName],
								["Patient's address", patient?.address],
								[
									"Patient's phone number",
									prettyprintPhoneNumber(
										patient?.phoneNumber,
										patient?.hasCommunications,
									),
								],
								["Patient's email", patient?.email],
								[
									"Patient's alternate contacts",
									<AlternateContactsList
										alternateContacts={patient?.alternateContacts}
										permission={team?.manageContacts}
									/>,
								],
								[
									"Agents",
									visit.fieldSupportAgents
										?.map(({ fullName }) => fullName)
										.join(", "),
								],
								[
									"Notes",
									<span className="whitespace-pre-line">{visit.notes}</span>,
								],
							]

							return (
								<li
									key={`li-${id}`}
									aria-label={`Patient ${patient?.fullName}`}
								>
									<Disclosure
										key={`disclosure-${id}`}
										name="visits"
										className={cn(
											isFocused(`visit-${id}`) && "animate-pulse bg-green-20",
										)}
										summary={
											<div
												className={cn(
													"grid w-full grid-cols-2 items-start gap-3 @6xl:grid-cols-[140px_240px_minmax(min-content,360px)_1fr] @6xl:items-center",
													canEdit &&
														"@6xl:grid-cols-[140px_240px_minmax(min-content,360px)_200px_1fr]",
												)}
											>
												<div className="truncate">{visitType}</div>

												<div className="@6xl:hidden" />

												<div className="text-navy-60 @6xl:hidden">
													Visit date & time:
												</div>

												<time className="text-navy-100 @6xl:text-navy-60">
													{`${visitDate.format("MMM D YYYY")}, ${prettyVisitInterval(visit)}, ${getTimeZone()}`}
												</time>

												<Communications
													visit={visit}
													className="hidden @6xl:flex"
												/>

												{canEdit && (
													<Actions visit={visit} className="hidden @6xl:flex" />
												)}
											</div>
										}
									>
										<div className="flex flex-col gap-4">
											<dl
												className="columns-md space-y-2"
												aria-label={`Visit details for ${patient?.fullName}`}
											>
												{details.map(([title, description]) => (
													<dt
														key={title as string}
														className="flex flex-wrap justify-between gap-1 @6xl:justify-start"
													>
														<span className="text-nowrap text-navy-60">
															{title}:
														</span>
														<span className="truncate">{description}</span>
													</dt>
												))}
											</dl>
											<div className="my-0 border border-dashed border-grey-20 @6xl:hidden" />
											<div className="space-y-4 @6xl:hidden">
												{canEdit && <Actions visit={visit} />}
												<Communications visit={visit} />
											</div>
										</div>
									</Disclosure>
								</li>
							)
						})}
					</ul>
				</div>
			</div>
		</Spinner>
	)
}

function Communications({
	visit,
	className,
	...props
}: ComponentProps<"div"> & { visit: Visit }) {
	if (!visit.patient) {
		return null
	}

	return (
		<div {...props} className={cn("flex gap-2", className)}>
			<Voice.CallToAction
				size="small"
				className="w-full"
				patient={visit.patient}
			/>

			<Chat.CallToAction
				view
				send
				size="small"
				className="w-full"
				patient={visit.patient}
				unreadMessagesCount={visit.unreadMessagesCount}
			/>
		</div>
	)
}

function Actions({
	visit,
	className,
	...props
}: ComponentProps<"div"> & {
	visit: Visit
}) {
	const { notify } = useNotification()
	const discloseEdit = useDisclosure()
	const discloseCancel = useDisclosure()
	const { focus } = useSpotlight()
	const handleEditSuccess = () => {
		discloseEdit.close()
		focus(`visit-${visit.id}`)
		notify.success({
			message: "Edit information",
			description:
				"The visit & patient's information were updated successfully",
		})
	}

	return (
		<div
			{...props}
			className={cn(
				"flex items-center justify-between @6xl:justify-start",
				className,
			)}
		>
			<Button type="link" className="size-fit px-0" onClick={discloseEdit.open}>
				Edit information
			</Button>

			<Divider vertical className="hidden @6xl:inline-block" />

			<Button
				type="link"
				className="size-fit px-0 text-yellow-orange-60"
				onClick={discloseCancel.open}
			>
				Unschedule
			</Button>

			{discloseEdit.isOpen && (
				<EditVisit.Modal
					open={discloseEdit.isOpen}
					onCancel={discloseEdit.close}
				>
					<EditVisit.Form visit={visit} onSuccess={handleEditSuccess} />
				</EditVisit.Modal>
			)}

			{discloseCancel.isOpen && (
				<CancelVisit
					visit={{ id: visit.id, status: "UNSCHEDULED" }}
					open={discloseCancel.isOpen}
					onCancel={discloseCancel.close}
					onSuccess={() => {
						discloseCancel.close()
						focus("needs-scheduling-tab")
						notify.success({
							message: "UnSchedule visit",
							description:
								"Visit unschedule successfully. This visit can be rescheduled again in the needs scheduling page.",
						})
					}}
					title="Do you want to unschedule this visit request?"
					okText="Yes, unschedule it"
				>
					This visit can be rescheduled again in the needs scheduling page.
				</CancelVisit>
			)}
		</div>
	)
}

const DEFAULT_SEARCH_PARAMS = {
	query: "",
	onlyUnreadMessages: "false",
}

const DEFAULT_SORT = [
	"visitDate-ascending",
	"unreadMessagesCount-ascending",
] as const
